import { supabase } from './supabase';

const adminService = {
  // Client Management
  getClients: async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select(`
          *,
          user_profiles(full_name, email),
          invoices(count),
          subscriptions(status, plan_name)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  createClient: async (clientData) => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .insert([clientData])
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  updateClient: async (clientId, updates) => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .update(updates)
        .eq('id', clientId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  deleteClient: async (clientId) => {
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Invoice Management
  getInvoices: async (filters = {}) => {
    try {
      let query = supabase
        .from('invoices')
        .select(`
          *,
          clients(company_name, email),
          transactions(status, amount, payment_method)
        `)
        .order('created_at', { ascending: false });

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.client_id) {
        query = query.eq('client_id', filters.client_id);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  createInvoice: async (invoiceData) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .insert([invoiceData])
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  updateInvoice: async (invoiceId, updates) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .update(updates)
        .eq('id', invoiceId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Transaction Management
  getTransactions: async (filters = {}) => {
    try {
      let query = supabase
        .from('transactions')
        .select(`
          *,
          invoices(invoice_number, client_id, clients(company_name))
        `)
        .order('created_at', { ascending: false });

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Dashboard Analytics
  getDashboardStats: async () => {
    try {
      const [clientsRes, invoicesRes, transactionsRes, revenueRes] = await Promise.all([
        supabase.from('clients').select('count', { count: 'exact' }),
        supabase.from('invoices').select('count', { count: 'exact' }),
        supabase.from('transactions').select('count', { count: 'exact' }),
        supabase.from('transactions').select('amount').eq('status', 'completed'),
      ]);

      const totalRevenue = revenueRes.data?.reduce((sum, t) => sum + parseFloat(t.amount), 0) || 0;

      return {
        success: true,
        data: {
          totalClients: clientsRes.count || 0,
          totalInvoices: invoicesRes.count || 0,
          totalTransactions: transactionsRes.count || 0,
          totalRevenue: totalRevenue,
        },
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Onboarding Management
  getOnboardingStatus: async () => {
    try {
      const { data, error } = await supabase
        .from('client_onboarding')
        .select(`
          *,
          clients(company_name, email)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  updateOnboardingStage: async (clientId, stage, completedSteps = []) => {
    try {
      const { data, error } = await supabase
        .from('client_onboarding')
        .update({ 
          stage, 
          completed_steps: completedSteps,
          updated_at: new Date().toISOString()
        })
        .eq('client_id', clientId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Subscription Management
  getSubscriptions: async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          *,
          clients(company_name, email)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

export default adminService;